import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Field, Form } from "components/forms";
import { useSelector, useDispatch } from "react-redux";
import { backendUrl } from 'config/envConfig';
import __ from 'utils/i18n';
import jwt_decode from 'jwt-decode';
import Icon from "@mdi/react";
import { mdiCheckCircle, mdiCloseCircle, mdiLoading } from "@mdi/js";
import { validateFields } from 'components/forms/validators';
import { logout } from "containers/Logout/actions";

const isTokenExpired = (token) => {
  try {
    const decoded = jwt_decode(token);
    const isExpired = decoded.exp * 1000 < Date.now();
    return isExpired;
  } catch (error) {
    return true;
  }
};

export const NewEquipmentModal = ({onSuccess}) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [loadingState, setLoadingState] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { subsidiaries } = useSelector((state) => state.login.user) || [];
  const { merchantId, subsidiaryId, subsidiaryName } = useSelector((state) => state.login.user);
  const subsidiariesList = subsidiaries || [{ subsidiaryId, subsidiaryName }];
  const { token } = useSelector(state => state.api);

  useEffect(() => {
    if (loadingState === 'success') {
      const timer = setTimeout(() => {
        window.location.reload();
        setLoadingState('');
        setShowModal(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loadingState]);

  const handleTokenExpiration = () => {
    if (isTokenExpired(token)) {
      dispatch(logout());
      return true;
    }
    return false;
  };

  const onSubmit = (data) => {
    setLoadingState('loading');
    setIsLoading(true);

    const bodyParams = {
      "merchant_id": merchantId,
      "user_code": data.code,
      "data": {
        "subsidiary": data.subsidiary,
        "alias": data.alias || ''
      }
    };

    fetch(`${backendUrl}/api/v1/payment-terminals`, {
      method: 'POST',
      proxy: false,
      headers: {
        'X-Authentication-Token': token,
        'x-request-download': 'stream',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bodyParams),
    })
      .then(response => {
        if (response.ok) {
          return { success: true, shouldLogout: false };
        } else if (response.status === 401) {
          const shouldLogout = handleTokenExpiration();
          return { success: false, shouldLogout };
        } else {
          return { success: false, shouldLogout: false };
        }
      })
      .then(result => {
        if (result.shouldLogout) {
          return;
        }
        setIsLoading(false);
        if (result.success) {
          onSuccess();
          setLoadingState('success');
        } else {
          setLoadingState('error');
        }
      })
      .catch(error => {
        setIsLoading(false);
        setLoadingState('error');
      });
  };

  return (
    <>
      <Button onClick={() => setShowModal(true)} color="primary">
        <i className="mdi mdi-plus mr-2" />
        {__("dashboard.sections.standalone.newPaymentTerminal")}
      </Button>

      <Form
        validate={validateFields([{ field: 'code', required: 1 }, { field: 'subsidiary', required: 1 }])}
      >
        {(form) => (
          <Modal
            className="reportWidgetModal"
            isOpen={showModal}
            toggle={() => setShowModal(false)}
            onClosed={() => setLoadingState('')}
          >
            <ModalHeader>
              {loadingState === '' && (
                __("dashboard.sections.standalone.modal.headerText")
              )}
            </ModalHeader>

            <ModalBody style={{paddingBottom:0}}>
              <div className="row">
                {loadingState === 'loading' && (
                  <div className="col-md-12">
                    <div className="text-center d-flex justify-content-center align-items-center">
                      <Icon path={mdiLoading} size={1} />
                    </div>
                    <div className="text-center">
                      <h3>{__("dashboard.sections.standalone.modal.loadingText")}</h3>
                      <p>{__("dashboard.sections.standalone.modal.loadingSubText")}</p>
                    </div>
                  </div>
                )}

                {loadingState === 'success' && (
                  <div className="col-md-12">
                    <div className="text-center text-success d-flex justify-content-center align-items-center mb-4">
                      <Icon path={mdiCheckCircle} size={4} />
                    </div>
                    <div className="text-center black">
                      <h3>{__("dashboard.sections.standalone.modal.successMessage")}</h3>
                      <p>{__("dashboard.sections.standalone.modal.successSubMessage")}</p>
                    </div>
                  </div>
                )}

                {loadingState === 'error' && (
                  <div className="col-md-12">
                    <div className="text-center text-danger d-flex justify-content-center align-items-center mb-4">
                      <Icon path={mdiCloseCircle} size={4} />
                    </div>
                    <div className="text-center">
                      <h3>{__("dashboard.sections.standalone.modal.errorMessage")}</h3>
                      <p>{__("dashboard.sections.standalone.modal.errorSubMessage")}</p>
                    </div>
                  </div>
                )}

                {loadingState === '' && (
                  <>
                    <div className="row">
                      <div className="text-center black">
                        {__("dashboard.sections.standalone.modal.secondaryTutileText")}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <Field
                        block
                        name={"code"}
                        type="text"
                        label={__("dashboard.sections.standalone.modal.inputTextCode")}
                        help={__("dashboard.sections.standalone.modal.placeholderTextCode")}
                        field={"code"}
                      />
                    </div>
                    <div className="col-md-12">
                      <Field
                        block
                        name={"subsidiary"}
                        field="subsidiary"
                        label={__("dashboard.sections.standalone.modal.inputSubsidiary")}
                        placeholder={__("dashboard.sections.standalone.modal.placeholderInputSubsidiary")}
                        type="select"
                        options={subsidiariesList && subsidiariesList.map(item => ({
                          value: item.subsidiaryId,
                          label: item.subsidiaryName
                        }))}
                      />
                    </div>
                  </>
                )}
              </div>
            </ModalBody>

            {loadingState === '' && (
              <ModalFooter>
                <Button onClick={() => setShowModal(false)}>
                  {__("actions.cancel")}
                </Button>

                <Button
                  color="primary"
                  isLoading={isLoading}
                  disabled={!form.values.code || !form.values.subsidiary || form.errors || isLoading}
                  onClick={() => {
                    onSubmit(form.values);
                  }}
                >
                  {__("discovery.buttons.next")}
                </Button>
              </ModalFooter>
            )}
          </Modal>
        )}
      </Form>
    </>
  );
};
